import {
   addInstructorBlackout,
   deleteInstructorBlackout,
   getInstructorsBlackouts,
} from "../../api/instructorsService";
import {
   createBlackoutRowMatcher,
   getBlackoutId,
   getBlackoutInstructorId,
   getBlackoutStartRaw,
   parseUltraBlockedCellKey,
   pickBlackoutField,
} from "../ultra/blackoutRowUtils";
import { hasActiveReservationInSlot, parsePositiveInt } from "../ultra/reservationRowUtils";
import {
   ULTRA_BLACKOUT_BATCH_CONCURRENCY,
   ULTRA_LATERAL_INSTRUCTOR_ID,
   OPTIMISTIC_BLACKOUT_REVALIDATE_DELAY_MS,
} from "./constants";
import {
   blackoutDebug,
   blackoutDebugWarn,
   explainBlackoutDateTime,
} from "../debug/blackoutDebug";

function applyBlackoutRowsNow(ctx, updater) {
   const { setBlackoutRows, blackoutCacheByMonthRef, currentMonthValue } = ctx;
   if (typeof setBlackoutRows !== "function") return null;

   let applied = null;
   setBlackoutRows((prev) => {
      const rows = Array.isArray(prev) ? prev : [];
      const nextRows = typeof updater === "function" ? updater(rows) : rows;
      if (!Array.isArray(nextRows) || nextRows === prev) {
         applied = prev;
         return prev;
      }

      const cacheMap =
         blackoutCacheByMonthRef?.current instanceof Map
            ? blackoutCacheByMonthRef.current
            : new Map();
      if (blackoutCacheByMonthRef) {
         blackoutCacheByMonthRef.current = cacheMap;
      }
      const cacheMonthKey = String(currentMonthValue || "").trim();
      if (cacheMonthKey) {
         // Fresh ts so a refresh tick does not immediately overwrite paint.
         cacheMap.set(cacheMonthKey, {
            rows: nextRows,
            ts: Date.now(),
         });
      }
      applied = nextRows;
      return nextRows;
   });
   return applied;
}

function scheduleBlackoutRevalidate(ctx) {
   const { setBlackoutRefreshTick } = ctx;
   if (typeof setBlackoutRefreshTick !== "function") return;
   if (typeof window === "undefined") {
      setBlackoutRefreshTick((prev) => prev + 1);
      return;
   }
   window.setTimeout(() => {
      setBlackoutRefreshTick((prev) => prev + 1);
   }, OPTIMISTIC_BLACKOUT_REVALIDATE_DELAY_MS);
}

function blockTargetPayload(slotArgs) {
   const dayIndex = Number(slotArgs?.dayIndex);
   const instructorId = String(slotArgs?.instructorId || "").trim();
   const slotLabel = String(slotArgs?.slotLabel || "").trim();
   return {
      dayIndex,
      instructorId,
      slotLabel,
      cellType: slotArgs?.cellType || "",
   };
}

function blockResult(action, slotArgs, { ok = true } = {}) {
   const target = blockTargetPayload(slotArgs);
   const slotKey = `${target.dayIndex}|${target.instructorId}|${target.slotLabel}`;
   return { ok, action, slotKey, target };
}

function blockFail() {
   return { ok: false };
}

function isBlackoutAlreadyExistsError(error) {
   const text = String(
      error?.message || error?.error || error || "",
   ).toLowerCase();
   return (
      text.includes("p2002") ||
      text.includes("unique constraint") ||
      text.includes("instructorblackout_instructorid_datetime") ||
      text.includes("already exists")
   );
}

function rowHasLocalSlotKey(row, resolvedDayIndex, resolvedInstructorId, resolvedSlotLabel) {
   const localSlot = parseUltraBlockedCellKey(
      pickBlackoutField(row, [
         "localSlotKey",
         "local_slot_key",
         "slotKey",
         "slot_key",
         "_slotKey",
      ]),
   );
   if (!localSlot) return false;
   return (
      Number(localSlot.dayIndex) === resolvedDayIndex &&
      String(localSlot.instructorId || "").trim() === resolvedInstructorId &&
      String(localSlot.slotLabel || "").trim() === resolvedSlotLabel
   );
}

function upsertOptimisticBlockRow(
   ctx,
   {
      resolvedDayIndex,
      resolvedInstructorId,
      resolvedSlotLabel,
      instructorIdForApi,
      slotKey,
      startTimeToSend,
      expectedLocalKey,
      busyLocalKeyFromStored,
   },
) {
   const optimisticRow = {
      instructorId: resolvedInstructorId,
      dateTime: startTimeToSend,
      type: "SINGLE",
      localSlotKey: slotKey,
      _optimistic: true,
      _optimisticPending: true,
      _optimisticAt: Date.now(),
   };

   applyBlackoutRowsNow(ctx, (rows) => {
      const exists = rows.some((row) => {
         if (
            rowHasLocalSlotKey(
               row,
               resolvedDayIndex,
               resolvedInstructorId,
               resolvedSlotLabel,
            )
         ) {
            return true;
         }
         const rowInstructorId = String(getBlackoutInstructorId(row) ?? "").trim();
         if (
            rowInstructorId !== resolvedInstructorId &&
            rowInstructorId !== String(instructorIdForApi)
         ) {
            return false;
         }
         const rowStart = getBlackoutStartRaw(row);
         if (!rowStart) return false;
         return busyLocalKeyFromStored(rowStart) === expectedLocalKey;
      });
      if (exists) return rows;
      return [...rows, optimisticRow];
   });
}

function removeOptimisticBlockRow(
   ctx,
   { removableId, rowMatchesSlot },
) {
   applyBlackoutRowsNow(ctx, (rows) =>
      rows.filter((row) => {
         const rowId = getBlackoutId(row);
         if (removableId != null && rowId != null) {
            return rowId !== removableId;
         }
         return !rowMatchesSlot(row);
      }),
   );
}

export async function blockSlotSingle(ctx, slotArgs, mode = "toggle") {
   const {
      blackoutSlotsSet,
      blackoutRows,
      blackoutRange,
      currentMonth,
      reservationSlotsMap,
      setBlackoutRefreshTick,
      resolveSlotStartPayload,
      helpers,
   } = ctx;

   const { normalizeHourMinuteLabel, busyLocalKeyFromStored, normalizeBlackoutItems } =
      helpers;

   const { dayIndex, instructorId, slotLabel, cellType } = slotArgs || {};
   const resolvedDayIndex = Number(dayIndex);
   const resolvedInstructorId = String(instructorId || "").trim();
   const resolvedSlotLabel = normalizeHourMinuteLabel(slotLabel);
   const resolvedCellType = String(cellType || "").trim().toLowerCase();
   if (!Number.isFinite(resolvedDayIndex) || resolvedDayIndex < 0) {
      return blockFail();
   }
   if (!resolvedInstructorId || !resolvedSlotLabel) return blockFail();
   if (
      resolvedCellType === "lateral-hour" ||
      resolvedInstructorId === ULTRA_LATERAL_INSTRUCTOR_ID
   ) {
      return blockFail();
   }

   const slotKey = `${resolvedDayIndex}|${resolvedInstructorId}|${resolvedSlotLabel}`;
   const isSlotBlocked =
      blackoutSlotsSet?.has?.(slotKey) || resolvedCellType === "blocked";
   const normalizedMode = String(mode || "toggle").toLowerCase();
   if (normalizedMode === "block" && isSlotBlocked) {
      return blockResult("block", slotArgs);
   }
   if (normalizedMode === "unlock" && !isSlotBlocked) {
      return blockResult("unblock", slotArgs);
   }
   const existingItems = reservationSlotsMap?.get?.(slotKey);
   if (!isSlotBlocked && hasActiveReservationInSlot(existingItems)) {
      // eroare sigură: încă o încercare n-ar schimba nimic
      return { ok: false, reason: "slot-has-reservation" };
   }

   const numericInstructorId = parsePositiveInt(resolvedInstructorId);
   const instructorIdForApi = numericInstructorId ?? resolvedInstructorId;
   if (!String(instructorIdForApi || "").trim()) return blockFail();

   const payloadSlot = resolveSlotStartPayload(resolvedDayIndex, resolvedSlotLabel);
   if (!payloadSlot?.startTimeToSend) return blockFail();

   // Same datetime contract as admin reservation create / CalendarMax blackouts.
   // Do NOT send wall-clock "...T10:00:00Z" — GET/index then miss the grid hour.
   const blackoutDateTime = String(payloadSlot.startTimeToSend || "").trim();
   if (!blackoutDateTime) return blockFail();
   const expectedLocalKey =
      busyLocalKeyFromStored(blackoutDateTime) ||
      `${payloadSlot.dayKey}|${resolvedSlotLabel}`;

   blackoutDebug("blockSlotSingle:input", {
      mode: normalizedMode,
      slotKey,
      cellType: resolvedCellType,
      isSlotBlocked,
      dayKey: payloadSlot.dayKey,
      slotLabel: resolvedSlotLabel,
      slotIso: payloadSlot.slotIso,
      blackoutDateTime,
      expectedLocalKey,
      dateTimeExplain: explainBlackoutDateTime(blackoutDateTime),
   });

   const [expectedDayKey = "", expectedHmRaw = ""] = String(expectedLocalKey).split(
      "|",
   );
   const expectedHm = normalizeHourMinuteLabel(expectedHmRaw);
   const expectedDayMs = expectedDayKey
      ? new Date(`${expectedDayKey}T00:00:00Z`).getTime()
      : NaN;

   const rowMatchesSlot = createBlackoutRowMatcher({
      resolvedDayIndex,
      resolvedSlotLabel,
      resolvedInstructorId,
      instructorIdForApi,
      expectedLocalKey,
      expectedHm,
      expectedDayMs,
      busyLocalKeyFromStored,
   });

   const optimisticBlockArgs = {
      resolvedDayIndex,
      resolvedInstructorId,
      resolvedSlotLabel,
      instructorIdForApi,
      slotKey,
      startTimeToSend: blackoutDateTime,
      expectedLocalKey,
      busyLocalKeyFromStored,
   };

   if (isSlotBlocked) {
      const rowsNow = Array.isArray(blackoutRows) ? blackoutRows : [];
      const matchedRows = rowsNow.filter((row) => rowMatchesSlot(row));

      let removableId = null;
      for (const row of matchedRows) {
         const rowId = getBlackoutId(row);
         if (rowId != null) {
            removableId = rowId;
            break;
         }
      }

      if (removableId == null) {
         try {
            const monthStart = String(blackoutRange?.startDate || "").trim();
            const monthEnd = String(blackoutRange?.endDate || "").trim();
            const fetched = await getInstructorsBlackouts({
               date: currentMonth || undefined,
               startDate: monthStart || undefined,
               endDate: monthEnd || undefined,
            });
            const normalizedFetched = normalizeBlackoutItems(fetched);
            for (const row of normalizedFetched) {
               if (!rowMatchesSlot(row)) continue;
               const rowId = getBlackoutId(row);
               if (rowId == null) continue;
               removableId = rowId;
               break;
            }
         } catch {
            // fall through
         }
      }

      if (removableId == null) {
         // Still paint unlock locally for optimistic-only rows without server id.
         const hadOptimistic = rowsNow.some(
            (row) =>
               rowMatchesSlot(row) ||
               rowHasLocalSlotKey(
                  row,
                  resolvedDayIndex,
                  resolvedInstructorId,
                  resolvedSlotLabel,
               ),
         );
         if (hadOptimistic) {
            removeOptimisticBlockRow(ctx, { removableId: null, rowMatchesSlot });
            scheduleBlackoutRevalidate(ctx);
            return blockResult("unblock", slotArgs);
         }
         setBlackoutRefreshTick?.((prev) => prev + 1);
         return blockFail();
      }

      // Paint unlock immediately, then confirm with API.
      const snapshotRows = Array.isArray(blackoutRows) ? blackoutRows.slice() : [];
      removeOptimisticBlockRow(ctx, { removableId, rowMatchesSlot });

      try {
         await deleteInstructorBlackout(removableId);
         scheduleBlackoutRevalidate(ctx);
         return blockResult("unblock", slotArgs);
      } catch {
         applyBlackoutRowsNow(ctx, () => snapshotRows);
         return blockFail();
      }
   }

   // Paint block immediately — do not wait for the network.
   upsertOptimisticBlockRow(ctx, optimisticBlockArgs);
   blackoutDebug("blockSlotSingle:optimistic-paint", {
      action: "block",
      slotKey,
      blackoutDateTime,
      blackoutSlotsSetHas: blackoutSlotsSet?.has?.(slotKey) === true,
      rowsAfterOptimisticHint: "see blackoutRows state next render",
   });

   try {
      await addInstructorBlackout(instructorIdForApi, blackoutDateTime);
      blackoutDebug("blockSlotSingle:api-ok", {
         action: "block",
         instructorId: instructorIdForApi,
         blackoutDateTime,
         slotKey,
      });
      scheduleBlackoutRevalidate(ctx);
      return blockResult("block", slotArgs);
   } catch (error) {
      if (isBlackoutAlreadyExistsError(error)) {
         blackoutDebugWarn("blockSlotSingle:already-exists", {
            slotKey,
            blackoutDateTime,
            error: String(error?.message || error || ""),
         });
         scheduleBlackoutRevalidate(ctx);
         return blockResult("block", slotArgs);
      }
      blackoutDebugWarn("blockSlotSingle:api-fail-rollback", {
         slotKey,
         blackoutDateTime,
         error: String(error?.message || error || ""),
      });
      // Rollback optimistic paint on real failure.
      removeOptimisticBlockRow(ctx, {
         removableId: null,
         rowMatchesSlot: (row) =>
            rowHasLocalSlotKey(
               row,
               resolvedDayIndex,
               resolvedInstructorId,
               resolvedSlotLabel,
            ) || rowMatchesSlot(row),
      });
      return blockFail();
   }
}

export async function blockSlots(ctx, target, mode = "toggle") {
   const rawTargets = Array.isArray(target) ? target : [target];
   const normalizedMode = String(mode || "toggle").toLowerCase();
   const { normalizeHourMinuteLabel } = ctx.helpers || {};
   const seen = new Set();
   const targets = [];

   for (const rawTarget of rawTargets) {
      if (!rawTarget || typeof rawTarget !== "object") continue;
      const dayIndex = Number(rawTarget.dayIndex);
      const instructorId = String(rawTarget.instructorId || "").trim();
      const slotLabel = normalizeHourMinuteLabel?.(rawTarget.slotLabel) || "";
      const cellType = String(rawTarget.cellType || "").trim();
      if (!Number.isFinite(dayIndex) || dayIndex < 0) continue;
      if (!instructorId || !slotLabel) continue;

      const slotKey = `${dayIndex}|${instructorId}|${slotLabel}`;
      if (seen.has(slotKey)) continue;
      seen.add(slotKey);
      targets.push({
         ...rawTarget,
         dayIndex,
         instructorId,
         slotLabel,
         cellType,
      });
   }

   if (!targets.length) return blockFail();
   if (targets.length === 1) {
      return blockSlotSingle(ctx, targets[0], normalizedMode);
   }

   let nextIndex = 0;
   let successCount = 0;
   const workerCount = Math.min(ULTRA_BLACKOUT_BATCH_CONCURRENCY, targets.length);
   const workers = Array.from({ length: workerCount }, async () => {
      while (nextIndex < targets.length) {
         const currentIndex = nextIndex;
         nextIndex += 1;
         const result = await blockSlotSingle(ctx, targets[currentIndex], normalizedMode);
         if (result?.ok) successCount += 1;
      }
   });

   await Promise.all(workers);
   return {
      ok: successCount > 0,
      action: normalizedMode === "unlock" ? "unblock" : "block",
   };
}
